import { FastlyImage } from '@components/common/image'
import Ratings from '@components/common/ratings'
import Partners from '@components/locale-pages/home/us/partners'
import PhoneInputContainer from '@containers/phone-input'
import useDetectCountry from '@hooks/useDetectCountry'
import { t, Trans } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import PriceSlotsOverview from './price-slots-overview'
import StudentCountriesOverview from './student-countries-overview'
import { RAMADAN_COUNTRY_CODES } from '@lib/data/country'
import FixedMobileCTA from '@components/course-landing-page/fixed-mobile-cta'

const RAMADAN_FOLD_IMAGE_PATH = `/images/ramadan-coding-camp/first-fold/`

const IMAGE_PATH = '/images/home/first-fold/'
const IMAGE_PROPS = {
  PK: {
    path: `${IMAGE_PATH}hero-banner-PK`,
    sizes: {
      height: 400,
      width: 600,
    },
  },
  BD: {
    path: `${IMAGE_PATH}hero-banner-BD`,
    sizes: {
      height: 400,
      width: 630,
    },
  },
  NG: {
    path: `${IMAGE_PATH}hero-banner-NG`,
    sizes: {
      height: 400,
      width: 630,
    },
  },
  UG: {
    path: `${IMAGE_PATH}hero-banner-UG`,
    sizes: {
      height: 400,
      width: 630,
    },
  },
  NP: {
    path: `${IMAGE_PATH}hero-banner-NP`,
    sizes: {
      height: 400,
      width: 630,
    },
  },
  US: {
    path: `${IMAGE_PATH}hero-banner-US`,
    sizes: {
      height: 397,
      width: 608,
    },
  },
  CA: {
    path: `${IMAGE_PATH}hero-banner-US`,
    sizes: {
      height: 397,
      width: 608,
    },
  },
  default: {
    path: `${IMAGE_PATH}hero-banner-IN`,
    sizes: {
      height: 400,
      width: 600,
    },
  },
}

type Props = {
  localeCountryCode?: string
}
/** i18n support added */
const FirstFold = ({ localeCountryCode }: Props) => {
  const [isFixedMobileCTAVisible, setIsFixedMobileCTAVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { countryCode } = useDetectCountry()
  const isCountryCodeFinalized = localeCountryCode || countryCode

  const heroImage =
    IMAGE_PROPS[localeCountryCode || countryCode] || IMAGE_PROPS['default']

  const isRamadanCountry = RAMADAN_COUNTRY_CODES.includes(countryCode)
  const ramadanCountry = {
    src: `${RAMADAN_FOLD_IMAGE_PATH}hero-banner`,
    width: 576,
    height: 379,
    alt: 'Ramadan coding class for kids background image',
  }

  const showFixedCTAOnScroll = (event: any) => {
    if (!containerRef.current) return
    const { offsetTop, offsetHeight } = containerRef.current
    const scrollTopPosition = event.target.documentElement.scrollTop
    setIsFixedMobileCTAVisible(offsetTop + offsetHeight < scrollTopPosition)
  }

  useEffect(() => {
    // Auto-focus on phone input field on page load
    document.getElementById('phone').focus()

    // Show fixed CTA upon scrolling on mobile view
    window.addEventListener('scroll', showFixedCTAOnScroll)
    return () => window.removeEventListener('scroll', showFixedCTAOnScroll)
  }, [])

  return (
    <>
      <FixedMobileCTA hideBtn={isFixedMobileCTAVisible} />
      <div
        className="relative flex justify-between px-5 md:px-3 pt-8 overflow-visible lg:rounded-b-lg hero-gradient-white-orange"
        ref={containerRef}
      >
        <div className="relaive container flex flex-col mx-auto text-grey z-10 lg:flex-row">
          <div className="z-10 flex flex-col items-center space-y-5 text-center lg:items-start lg:text-left">
            <h1 className="max-w-2xl text-center font-600 lg:text-left custom-home-h1">
              <Trans>
                Top-rated{' '}
                <span className="underlined-text">
                  online AI & coding classes
                </span>{' '}
                for kids and teens to become the innovators of tomorrow
              </Trans>
            </h1>
            <div className="flex flex-col justify-center items-center text-base lg:text-xl lg:items-start">
              <div className="mb-1">
                <p>
                  <svg
                    className="inline mr-2 lg:mr-3 w-3 lg:w-4"
                    width="16"
                    height="13"
                    viewBox="0 0 16 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.99999 3.37012C6.44759 3.37012 5.18921 4.77116 5.18921 6.49974C5.18921 8.22809 6.44759 9.62937 7.99999 9.62937C9.55238 9.62937 10.811 8.22809 10.811 6.49974C10.811 4.77116 9.55238 3.37012 7.99999 3.37012Z"
                      fill="#FF5A43"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.08855 2.13025C5.34531 2.40824 5.35123 2.86557 5.10146 3.15135C3.48853 4.9984 3.48853 8.0013 5.10146 9.84835C5.35123 10.1344 5.34531 10.5915 5.08855 10.8695C4.83179 11.1477 4.42115 11.1411 4.17138 10.8551C2.06885 8.44755 2.06885 4.55215 4.17138 2.14464C4.42115 1.85863 4.83179 1.85202 5.08855 2.13025ZM10.9114 2.13025C11.1682 1.85202 11.5788 1.85863 11.8286 2.14464C13.9309 4.55215 13.9309 8.44755 11.8286 10.8551C11.5788 11.1411 11.1682 11.1477 10.9114 10.8695C10.6544 10.5915 10.6487 10.1344 10.8985 9.84835C12.5112 8.0013 12.5112 4.9984 10.8985 3.15135C10.6487 2.86557 10.6544 2.40824 10.9114 2.13025Z"
                      fill="#FF5A43"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.2609 0.212062C3.51406 0.494299 3.51363 0.951637 3.26005 1.2334C0.643162 4.14215 0.643162 8.85782 3.26005 11.7666C3.51363 12.0486 3.51406 12.5057 3.2609 12.7881C3.00774 13.0704 2.5971 13.0706 2.34352 12.7889C-0.781172 9.31587 -0.781172 3.6841 2.34352 0.211118C2.5971 -0.0706476 3.00774 -0.0704116 3.2609 0.212062ZM12.7391 0.212062C12.9923 -0.0704116 13.4029 -0.0706476 13.6565 0.211118C16.7812 3.6841 16.7812 9.31587 13.6565 12.7889C13.4029 13.0706 12.9923 13.0704 12.7391 12.7881C12.4862 12.5057 12.4866 12.0486 12.7399 11.7666C15.357 8.85782 15.357 4.14215 12.7399 1.2334C12.4866 0.951637 12.4862 0.494299 12.7391 0.212062Z"
                      fill="#FF5A43"
                    />
                  </svg>
                  <Trans>Engaging live video lessons</Trans>
                </p>
              </div>
              <div className="mb-1">
                <p>
                  <svg
                    className="inline mr-2 lg:mr-3 w-3 lg:w-4"
                    width="16"
                    height="24"
                    viewBox="0 0 16 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z"
                      stroke="#8B78FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.21 13.8899L3 22.9999L8 19.9999L13 22.9999L11.79 13.8799"
                      stroke="#8B78FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="8"
                      cy="8"
                      r="3.6"
                      stroke="#8B78FF"
                      strokeWidth="0.8"
                    />
                    <path
                      d="M7.64574 8.85445L9.37746 6.0903L9.37774 6.08986C9.39335 6.06524 9.41107 6.0421 9.43101 6.02092L9.43101 6.02092C9.4511 5.99959 9.47261 5.98119 9.49519 5.96526L9.49617 5.96457L9.49618 5.96458C9.51956 5.94851 9.54429 5.93518 9.57016 5.92476L9.57043 5.92465C9.59716 5.91398 9.62416 5.90696 9.65168 5.90307L9.65256 5.90295L9.65256 5.90295C9.68017 5.8993 9.70833 5.89896 9.73667 5.90236M7.64574 8.85445L5.91312 7.59221C5.90699 7.62045 5.90302 7.64923 5.90113 7.67828L5.90113 7.67828L5.90109 7.67888C5.89942 7.70716 5.89956 7.73637 5.90225 7.76594M7.64574 8.85445L6.59466 7.38291L6.5948 7.38281L6.59119 7.37834C6.57389 7.35684 6.55481 7.33689 6.53348 7.3189C6.51254 7.30124 6.49015 7.28586 6.4663 7.27301L6.4663 7.27301L6.46518 7.27242C6.4417 7.26016 6.41693 7.2502 6.3909 7.24316M7.64574 8.85445L6.03102 7.54566L5.93761 7.50995C5.948 7.48278 5.9604 7.45725 5.97475 7.433C5.98932 7.40837 6.00557 7.38592 6.02356 7.36508L6.02395 7.36462L6.02396 7.36462C6.04203 7.34393 6.06176 7.32502 6.08341 7.30818L6.08378 7.3079L6.08378 7.3079C6.1057 7.29101 6.12859 7.27699 6.15247 7.26543L6.1536 7.26489L6.1536 7.26489C6.17751 7.25369 6.203 7.24461 6.23013 7.23881L6.2303 7.23878C6.25659 7.23321 6.28316 7.23065 6.31003 7.23121L6.31108 7.23124L6.31108 7.23124C6.3372 7.23207 6.36382 7.23567 6.3909 7.24316M9.73667 5.90236C9.76479 5.90554 9.79217 5.91235 9.81836 5.92222M9.73667 5.90236C9.73682 5.90237 9.73696 5.90239 9.73711 5.90241L9.72509 6.00169L9.73621 5.90231C9.73636 5.90232 9.73651 5.90234 9.73667 5.90236ZM9.81836 5.92222C9.84561 5.93229 9.87074 5.94569 9.89391 5.9612M9.81836 5.92222C9.8185 5.92228 9.81865 5.92233 9.81879 5.92239L9.7834 6.01592L9.81793 5.92207C9.81807 5.92212 9.81822 5.92217 9.81836 5.92222ZM9.89391 5.9612C9.91793 5.97717 9.93976 5.99584 9.95939 6.01595M9.89391 5.9612C9.89396 5.96123 9.89401 5.96127 9.89407 5.9613L9.83841 6.04438L9.89375 5.96109C9.89381 5.96113 9.89386 5.96116 9.89391 5.9612ZM9.95939 6.01595C9.97941 6.03639 9.99704 6.05868 10.0126 6.08225L10.0129 6.08266C10.0283 6.10624 10.042 6.13129 10.0536 6.15777L10.054 6.15865L10.054 6.15865C10.0653 6.18482 10.0746 6.21209 10.0819 6.24061M9.95939 6.01595C9.95942 6.01598 9.95945 6.01601 9.95948 6.01604L9.88789 6.08587M9.95939 6.01595C9.95936 6.01593 9.95933 6.0159 9.9593 6.01587L9.88789 6.08587M10.0819 6.24061C10.0895 6.26964 10.0945 6.29889 10.0973 6.328C10.1001 6.35757 10.1007 6.38696 10.0992 6.41629L10.0992 6.41693C10.0975 6.44632 10.0937 6.47558 10.0875 6.50463C10.0815 6.53322 10.0734 6.56113 10.0629 6.58862L10.0628 6.5889C10.0525 6.61574 10.0399 6.64206 10.0248 6.66719L10.0238 6.66893L10.0237 6.66891L7.9834 9.909L7.98326 9.90922C7.9662 9.93615 7.94677 9.96125 7.9248 9.98396L7.92448 9.98429C7.90221 10.0071 7.87803 10.0268 7.8522 10.0433L7.85189 10.0435C7.82597 10.06 7.79798 10.0737 7.76795 10.0834C7.73817 10.093 7.70778 10.0983 7.67738 10.0997L7.67646 10.0998L7.67646 10.0998C7.64608 10.1009 7.61542 10.0981 7.58508 10.091L7.58422 10.0908C7.55482 10.0836 7.52592 10.0728 7.49802 10.0577L7.54558 9.96978L7.49831 10.0579C7.47104 10.0432 7.44554 10.0254 7.42178 10.004C7.39834 9.98288 7.37736 9.95921 7.35859 9.93306L7.35851 9.93295L5.98516 8.01355L5.98196 8.00909L5.98209 8.00901C5.96678 7.98492 5.95382 7.95958 5.94288 7.93346L5.94277 7.93319C5.93183 7.90686 5.92305 7.87954 5.91627 7.85198L5.91622 7.85179C5.90929 7.82339 5.90472 7.79474 5.90225 7.76594M10.0819 6.24061C10.082 6.24072 10.082 6.24082 10.082 6.24092L9.98514 6.26566L10.0819 6.24031C10.0819 6.24041 10.0819 6.24051 10.0819 6.24061ZM5.90225 7.76594C5.90224 7.76583 5.90223 7.76572 5.90222 7.76561L6.00186 7.75712L5.90228 7.76626C5.90227 7.76615 5.90226 7.76604 5.90225 7.76594ZM9.88789 6.08587C9.90302 6.1013 9.91674 6.11854 9.92919 6.13738L9.88789 6.08587ZM7.43984 9.87476L6.06648 7.95536L7.43984 9.87476ZM7.43984 9.87476C7.45465 9.8954 7.47089 9.91364 7.4887 9.92968C7.5065 9.94571 7.52542 9.95894 7.54559 9.96976C7.56561 9.98059 7.58641 9.9884 7.60784 9.99362M7.43984 9.87476L7.60784 9.99362M7.60784 9.99362C7.62928 9.99863 7.65103 10.0006 7.67278 9.99983C7.69453 9.99883 7.71612 9.99502 7.73724 9.9882C7.75836 9.98139 7.77869 9.97157 7.79823 9.95914C7.81761 9.94671 7.8359 9.93188 7.85292 9.91444M7.60784 9.99362L7.85292 9.91444M7.85292 9.91444C7.86978 9.897 7.88507 9.87736 7.89878 9.85571L9.93912 6.61563C9.95094 6.59599 9.96102 6.57494 9.96953 6.55289C9.97804 6.53064 9.98466 6.50779 9.98971 6.48394C9.99475 6.46009 9.9979 6.43583 9.99932 6.41118L7.85292 9.91444ZM6.3909 7.24316C6.39103 7.24319 6.39116 7.24323 6.39128 7.24326L6.36451 7.33961L6.39053 7.24305C6.39065 7.24309 6.39078 7.24312 6.3909 7.24316Z"
                      fill="#8B78FF"
                      stroke="#8B78FF"
                      strokeWidth="0.2"
                    />
                  </svg>
                  <Trans>Learn from top-rated, world-class instructors</Trans>
                </p>
              </div>
              <div>
                <p>
                  <svg
                    className="inline mr-2 lg:mr-3 w-3 lg:w-4"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.9967 0H12.9494L13.8257 0.873472L0.268859 14.4326C-0.0896196 14.7912 -0.0896196 15.3724 0.268859 15.731C0.627337 16.0897 1.20831 16.0897 1.56679 15.731L15.1269 2.17194L15.9967 3.03862V0ZM8.30203 0.00329905H9.21161V3.03862L8.34199 2.17194L2.23079 8.28566C1.87212 8.64428 1.29134 8.64428 0.932858 8.28566C0.574186 7.92684 0.574186 7.34582 0.932858 6.98719L7.04076 0.876771L6.16435 0.00329905H8.30203ZM15.0904 6.77799H16V9.81642L15.1304 8.94974L9.01918 15.0602C8.66051 15.4188 8.07973 15.4188 7.72125 15.0602C7.36258 14.7015 7.36258 14.1203 7.72125 13.7617L13.829 7.65127L12.9527 6.77799H15.0904Z"
                      fill="#FF9F59"
                    />
                  </svg>
                  <Trans>Boost confidence with measurable progress</Trans>
                </p>
              </div>
            </div>

            {/* phone number input */}
            <div className="text-left w-full sm:w-auto pt-3">
              <PhoneInputContainer
                isErrorDarkRed
                isAutoFocus
                buttonText={t`Try a free lesson`}
                isInHomePage
                countryCode={localeCountryCode}
              />
              <div className="mt-3 mb-3 lg:mb-8">
                <PriceSlotsOverview />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-8 space-y-5 md:space-y-0 justify-center lg:justify-start pb-6 lg:pb-0">
              <StudentCountriesOverview isTextWhite={false} />
              <Ratings isDarkText />
            </div>
          </div>
          <div className="relative flex flex-col items-center justify-center mx-auto lg:max-w-half">
            {isCountryCodeFinalized &&
              (isRamadanCountry ? (
                <FastlyImage
                  src={`${ramadanCountry.src}.png`}
                  webpSrc={`${ramadanCountry.src}.webp`}
                  width={ramadanCountry.width}
                  height={ramadanCountry.height}
                  alt={ramadanCountry.alt}
                  priority
                  loading="eager"
                />
              ) : (
                <FastlyImage
                  src={`${heroImage.path}.png`}
                  webpSrc={`${heroImage.path}.webp`}
                  width={heroImage.sizes.width}
                  height={heroImage.sizes.height}
                  alt={t`Children learning to code through online coding classes`}
                  priority
                  loading="eager"
                />
              ))}
          </div>
        </div>
      </div>
      <div className="relative">
        <Partners />
      </div>
    </>
  )
}

export default FirstFold
